import React, { useMemo, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Button,
    Chip,
    TextField,
    FormControlLabel,
    Checkbox,
    Divider,
} from "@mui/material";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Skeleton from "react-loading-skeleton";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment";
import { doc, getDoc, updateDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { DATABASE, AUTH_LOGIN } from "../../../Config/firebase";

/**
 * Account Operations Freeze — bloqueia saque/depósito/toggle Yield de um cliente
 * sem update do app. Cliente continua logando e vendo o saldo; overlay institucional
 * no app bloqueia as ações. Ver docs/ACCOUNT_OPERATIONS_FREEZE.md (spec canônica).
 */
const fieldLabelSx = {
    fontSize: "12px",
    fontWeight: 500,
    color: "text.secondary",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    fontFamily: "Poppins",
    mb: 0.35,
};

const fieldValueSx = {
    fontSize: "14px",
    fontWeight: 500,
    color: "text.primary",
    fontFamily: "Poppins",
    lineHeight: 1.45,
    wordBreak: "break-word",
};

const fmt = (ts) => {
    if (!ts) return "—";
    try {
        const date = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
        return moment(date).format("DD MMM YYYY, HH:mm");
    } catch {
        return "—";
    }
};

const Field = ({ label, children, xs = 12, md = 6 }) => (
    <Grid item xs={xs} md={md}>
        <Typography sx={fieldLabelSx}>{label}</Typography>
        <Box sx={fieldValueSx}>{children}</Box>
    </Grid>
);

const AccountOperationsFreeze = ({ customer, isLoading, onChanged }) => {
    const [reason, setReason] = useState("");
    const [sendEmail, setSendEmail] = useState(true);
    const [sendUnfreezeEmail, setSendUnfreezeEmail] = useState(true);
    const [busy, setBusy] = useState(false);

    const isFrozen = customer?.account_operations_frozen === true;
    const docId = customer?.doc_id;

    const history = useMemo(
        () => (Array.isArray(customer?.account_operations_freeze_history)
            ? customer.account_operations_freeze_history
            : []),
        [customer]
    );

    const complianceEmailLabel = (() => {
        if (customer?.account_operations_frozen_notify === false) return "Not requested";
        if (customer?.account_operations_frozen_email_sent === true) {
            return `Sent · ${fmt(customer?.account_operations_frozen_email_sent_at)}`;
        }
        return "Pending send";
    })();

    const handleFreeze = async () => {
        if (!docId) return;
        const trimmedReason = reason.trim();
        const confirm = await Swal.fire({
            title: "Freeze account operations?",
            html: `The client will keep seeing their balance but <b>cannot deposit, withdraw or toggle Yield</b>.<br/>Yield will be turned OFF (no "Yield Deactivated" email).<br/>${
                sendEmail ? "A compliance email <b>will</b> be sent to the client." : "No compliance email will be sent to the client."
            }<br/><br/>Admins will receive a confirmation email either way.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Freeze",
            confirmButtonColor: "#ed6c02",
            cancelButtonText: "Cancel",
        });
        if (!confirm.isConfirmed) return;

        setBusy(true);
        try {
            const userRef = doc(DATABASE, "users", docId);
            const snap = await getDoc(userRef);
            const data = snap.data() || {};
            const currentYieldActive = data.yield_active === true;
            const currentHistory = Array.isArray(data.account_operations_freeze_history)
                ? data.account_operations_freeze_history
                : [];
            const admin = AUTH_LOGIN.currentUser?.email || "admin";

            const newInterval = {
                frozen_at: Timestamp.now(),
                frozen_by: admin,
                reason: trimmedReason,
                unfrozen_at: null,
                unfrozen_by: null,
            };

            const updates = {
                account_operations_frozen: true,
                account_operations_frozen_at: serverTimestamp(),
                account_operations_frozen_by: admin,
                account_operations_frozen_reason: trimmedReason,
                account_operations_frozen_notify: sendEmail,
                account_operations_frozen_email_sent: false,
                account_operations_frozen_email_sent_at: null,
                account_operations_frozen_yield_snapshot: currentYieldActive,
                account_operations_freeze_history: [...currentHistory, newInterval],
            };

            if (currentYieldActive) {
                updates.yield_active = false;
                updates.yield_deactivation_source = "account_freeze";
            }

            await updateDoc(userRef, updates);
            toast.success("Account operations frozen");
            setReason("");
            if (onChanged) await onChanged(docId);
        } catch (error) {
            console.error("Error freezing account:", error);
            toast.error("Failed to freeze account");
        } finally {
            setBusy(false);
        }
    };

    const handleUnfreeze = async () => {
        if (!docId) return;
        const confirm = await Swal.fire({
            title: "Unfreeze account operations?",
            html: `The client will be able to deposit, withdraw and toggle Yield again.<br/><b>Yield will NOT be re-enabled automatically</b> — the client must turn it back on.<br/><br/>${
                sendUnfreezeEmail
                    ? "A restoration email <b>will</b> be sent to the client."
                    : "No restoration email will be sent to the client."
            }<br/>Admins will receive a confirmation email either way.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Unfreeze",
            confirmButtonColor: "#2e7d32",
            cancelButtonText: "Cancel",
        });
        if (!confirm.isConfirmed) return;

        setBusy(true);
        try {
            const userRef = doc(DATABASE, "users", docId);
            const snap = await getDoc(userRef);
            const data = snap.data() || {};
            const admin = AUTH_LOGIN.currentUser?.email || "admin";
            const currentHistory = Array.isArray(data.account_operations_freeze_history)
                ? [...data.account_operations_freeze_history]
                : [];

            for (let i = currentHistory.length - 1; i >= 0; i--) {
                if (currentHistory[i] && currentHistory[i].unfrozen_at == null) {
                    currentHistory[i] = {
                        ...currentHistory[i],
                        unfrozen_at: Timestamp.now(),
                        unfrozen_by: admin,
                    };
                    break;
                }
            }

            await updateDoc(userRef, {
                account_operations_frozen: false,
                account_operations_unfrozen_at: serverTimestamp(),
                account_operations_unfrozen_by: admin,
                account_operations_freeze_history: currentHistory,
                account_operations_unfrozen_notify: sendUnfreezeEmail,
                // Reset so CF can send (or skip) the client email based on notify.
                account_operations_unfrozen_email_sent: false,
                account_operations_unfrozen_email_sent_at: null,
            });
            toast.success("Account operations unfrozen");
            setSendUnfreezeEmail(true);
            if (onChanged) await onChanged(docId);
        } catch (error) {
            console.error("Error unfreezing account:", error);
            toast.error("Failed to unfreeze account");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box
            sx={{
                mt: 2,
                p: { xs: 2, md: 2.5 },
                borderRadius: "12px",
                border: "1px solid",
                borderColor: isFrozen ? "#0288d1" : "divider",
                bgcolor: isFrozen ? "rgba(2,136,209,0.12)" : "background.paper",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                <Typography
                    sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "text.primary",
                        fontFamily: "Poppins",
                    }}
                >
                    Account Operations
                </Typography>
                {isLoading ? (
                    <Skeleton width={110} height={28} />
                ) : isFrozen ? (
                    <Chip
                        icon={<AcUnitIcon sx={{ color: "#fff !important", fontSize: "16px !important" }} />}
                        label="Frozen"
                        size="small"
                        sx={{ color: "#fff", backgroundColor: "#0288d1", fontWeight: 600, fontFamily: "Poppins" }}
                    />
                ) : (
                    <Chip
                        label="Active"
                        size="small"
                        sx={{ color: "#fff", backgroundColor: "#4CAF50", fontWeight: 600, fontFamily: "Poppins" }}
                    />
                )}
            </Box>

            {isLoading ? (
                <Skeleton height={120} />
            ) : isFrozen ? (
                <Box>
                    <Grid container spacing={2}>
                        <Field label="Frozen at">{fmt(customer?.account_operations_frozen_at)}</Field>
                        <Field label="Frozen by">{customer?.account_operations_frozen_by || "—"}</Field>
                        <Field label="Reason (internal)" xs={12} md={12}>
                            {customer?.account_operations_frozen_reason?.trim()
                                ? customer.account_operations_frozen_reason
                                : "—"}
                        </Field>
                        <Field label="Yield before freeze">
                            {customer?.account_operations_frozen_yield_snapshot === true ? "Was ON" : "Was OFF"}
                        </Field>
                        <Field label="Compliance email to client">{complianceEmailLabel}</Field>
                    </Grid>

                    <FormControlLabel
                        sx={{ mt: 2, display: "block" }}
                        control={
                            <Checkbox
                                checked={sendUnfreezeEmail}
                                onChange={(e) => setSendUnfreezeEmail(e.target.checked)}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                Send restoration email to client on unfreeze
                            </Typography>
                        }
                    />

                    <Button
                        variant="contained"
                        startIcon={<LockOpenIcon />}
                        onClick={handleUnfreeze}
                        disabled={busy}
                        sx={{
                            mt: 2.5,
                            color: "#fff !important",
                            // Theme uses `background` gradient on contained — must override that, not only backgroundColor
                            background: "#2e7d32 !important",
                            backgroundColor: "#2e7d32 !important",
                            backgroundImage: "none !important",
                            boxShadow: "none",
                            "&:hover": {
                                background: "#1b5e20 !important",
                                backgroundColor: "#1b5e20 !important",
                                backgroundImage: "none !important",
                                boxShadow: "none",
                            },
                            "&.Mui-disabled": {
                                background: "#a5d6a7 !important",
                                color: "#fff !important",
                            },
                            textTransform: "none",
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            px: 2.5,
                        }}
                    >
                        Unfreeze account
                    </Button>
                </Box>
            ) : (
                <Box>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            color: "text.secondary",
                            fontFamily: "Poppins",
                            lineHeight: 1.55,
                            mb: 2,
                        }}
                    >
                        Freezing blocks deposits, withdrawals and Yield toggles. The client keeps logging in and seeing
                        their balance. In-flight requests are <strong>not</strong> cancelled — review them manually.
                        Admins always get a confirmation email; client compliance email is optional below.
                    </Typography>
                    <TextField
                        label="Reason (internal — not shown to client)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        sx={{ mb: 1 }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />}
                        label={
                            <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", color: "text.primary" }}>
                                Send compliance email to client
                            </Typography>
                        }
                    />
                    <Box>
                        <Button
                            variant="contained"
                            startIcon={<AcUnitIcon />}
                            onClick={handleFreeze}
                            disabled={busy}
                            sx={{
                                mt: 1.5,
                                color: "#fff !important",
                                // Theme uses `background` gradient on contained — must override that, not only backgroundColor
                                background: "#ed6c02 !important",
                                backgroundColor: "#ed6c02 !important",
                                backgroundImage: "none !important",
                                boxShadow: "none",
                                "&:hover": {
                                    background: "#e65100 !important",
                                    backgroundColor: "#e65100 !important",
                                    backgroundImage: "none !important",
                                    boxShadow: "none",
                                },
                                "&.Mui-disabled": {
                                    background: "#ffcc80 !important",
                                    color: "#fff !important",
                                },
                                textTransform: "none",
                                fontFamily: "Poppins",
                                fontWeight: 600,
                                px: 2.5,
                            }}
                        >
                            Freeze account
                        </Button>
                    </Box>
                </Box>
            )}

            {history.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography sx={{ ...fieldLabelSx, mb: 1 }}>
                        Freeze history ({history.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {history.slice().reverse().map((h, idx) => {
                            const isOpen = h?.unfrozen_at == null;
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.25,
                                        py: 1,
                                        borderRadius: "8px",
                                        bgcolor: "background.default",
                                        border: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "13px", fontFamily: "Poppins", color: "text.primary", flex: "1 1 200px" }}>
                                        {fmt(h?.frozen_at)}
                                        {" → "}
                                        {isOpen ? "…" : fmt(h.unfrozen_at)}
                                    </Typography>
                                    <Chip
                                        label={isOpen ? "Open" : "Closed"}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: "#fff",
                                            backgroundColor: isOpen ? "#0288d1" : "#6b7280",
                                            "& .MuiChip-label": { px: 1 },
                                        }}
                                    />
                                    {h?.reason ? (
                                        <Typography sx={{ fontSize: "12px", color: "text.secondary", fontFamily: "Poppins", width: "100%" }}>
                                            {h.reason}
                                        </Typography>
                                    ) : null}
                                    {h?.frozen_by ? (
                                        <Typography sx={{ fontSize: "11px", color: "text.secondary", fontFamily: "Poppins", width: "100%", opacity: 0.85 }}>
                                            by {h.frozen_by}
                                            {h?.unfrozen_by ? ` · unfrozen by ${h.unfrozen_by}` : ""}
                                        </Typography>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default AccountOperationsFreeze;
